import { ApiError, failureFrom } from './errors';

/**
 * Who the browser is (ADR-0070).
 *
 * `GET /auth/me` on load; a 401 means sign in, for every reason at once — no
 * cookie, an expired session, revoked access, the kill switch — and the
 * interface does not try to tell them apart. Sign-in itself is a link: the
 * gateway sends the person to Google and sets an HttpOnly cookie on the way
 * back, so there is no token to hold and none to store.
 */
export interface Me {
  humanId: string;
  email: string;
  displayName: string;
  /** The department every read is scoped to. One at a time. */
  department: string;
  /** Every department they may act in. The switcher's list. */
  departments: string[];
  /** The ones they lead. Whose approvals are theirs; advisory here. */
  leads: string[];
  isSuperadmin: boolean;
  expiresAt: string;
  /**
   * True only for a superadmin while the platform-wide stop is engaged —
   * nobody else's session reads while it is. The shell opens on Safety.
   */
  killSwitchEngaged?: boolean;
}

export async function fetchMe(): Promise<Me> {
  const response = await fetch('/auth/me', { credentials: 'same-origin' });
  if (!response.ok) throw new ApiError(await failureFrom(response, '/auth/me'));
  const me = (await response.json()) as Partial<Me>;
  return { ...me, leads: me.leads ?? [] } as Me;
}

export async function switchDepartment(department: string): Promise<{ department: string }> {
  const response = await fetch('/auth/department', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ department }),
  });
  if (!response.ok) throw new ApiError(await failureFrom(response, '/auth/department'));
  return (await response.json()) as { department: string };
}

export async function signOut(): Promise<void> {
  await fetch('/auth/signout', { method: 'POST', credentials: 'same-origin' });
}

/** Where to send somebody to sign in, coming back to where they were. */
export function signInUrl(returnTo: string): string {
  return `/auth/start?returnTo=${encodeURIComponent(returnTo)}`;
}

/**
 * The development sign-in, which exists only on a loopback gateway that was
 * told it is local (`apps/gateway/src/local.ts`). Offered on the sign-in page
 * only when the page itself is on a loopback host, so a deployed interface
 * never shows a door that is not there.
 */
export function devSignInUrl(email: string, returnTo: string): string {
  return `/auth/dev?email=${encodeURIComponent(email)}&returnTo=${encodeURIComponent(returnTo)}`;
}

export function isLoopback(hostname: string): boolean {
  return ['localhost', '127.0.0.1', '[::1]', '::1'].includes(hostname.toLowerCase());
}
